<script setup lang="ts">
import type { CharacterReference } from '~/types'

defineProps<{ characterInfo: CharacterReference }>()
const [DefineRatingTemplate, ReuseRatingTemplate] = createReusableTemplate()
const [DefineInfoTemplate, ReuseInfoTemplate] = createReusableTemplate()
const [DefineQuestionTemplate, ReuseQuestionTemplate] = createReusableTemplate()

const isOpen = ref(false)
</script>

<template>
  <DefineRatingTemplate v-slot="{ title, rating }">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-x-2">
        <GIcon name="i-ph-info" />
        <span>{{ title }}:</span>
      </div>
      <CommonStarRating :rating />
    </div>
  </DefineRatingTemplate>
  <DefineInfoTemplate v-slot="{ icon, content }">
    <div v-if="content" class="w-full flex items-center gap-x-4">
      <GIcon :name="icon" />
      <p>{{ content }}</p>
    </div>
  </DefineInfoTemplate>
  <DefineQuestionTemplate v-slot="{ question, answer }">
    <div class="space-y-2">
      <div class="flex items-center gap-x-1">
        <GIcon name="i-ph-circle-duotone" size="xs" class="text-primary" />
        <p>{{ question }}</p>
      </div>
      <div
        card="~ padded"
        class="bg-muted"
      >
        <p class="text-sm">
          <i>{{ answer ?? "-" }}</i>
        </p>
      </div>
    </div>
  </DefineQuestionTemplate>
  <GCollapsible
    v-model:open="isOpen"
    card="~"
    class="w-full overflow-hidden"
  >
    <GCollapsibleTrigger as-child>
      <GButton
        btn="ghost-gray"
        class="w-full flex items-center justify-between rounded-none py-6"
      >
        <div class="flex items-center gap-x-4">
          <GIcon name="i-ph-user" size="xl" :class="[isOpen && 'text-primary']" />
          <h3>{{ characterInfo.name }}</h3>
        </div>
        <GIcon name="i-ph-caret-left" :class="[isOpen && 'rotate-90 text-primary']" />
      </GButton>
    </GCollapsibleTrigger>
    <GCollapsibleContent class="p-4 space-y-4">
      <div class="space-y-2">
        <ReuseInfoTemplate icon="i-ph-users" :content="characterInfo.relationship" />
      </div>
      <GSeparator />
      <div class="space-y-2">
        <h3>Ratings</h3>
        <ReuseRatingTemplate title="Work Rating" :rating="characterInfo.feedback?.workQuality" />
        <ReuseRatingTemplate title="Reliability" :rating="characterInfo.feedback?.reliability" />
        <ReuseRatingTemplate title="Communication Skills" :rating="characterInfo.feedback?.communicationSkills" />
        <ReuseRatingTemplate title="Teamwork and Collaboration" :rating="characterInfo.feedback?.teamworkCollaboration" />
        <ReuseRatingTemplate title="Problem Solving Skills" :rating="characterInfo.feedback?.problemSolving" />
      </div>
      <GSeparator />
      <div class="space-y-2">
        <h3>Feedbacks</h3>
        <div class="space-y-6">
          <ReuseQuestionTemplate question="How would you describe your overall experience working with the applicant?" :answer="characterInfo.feedback?.workingExperience" />
          <ReuseQuestionTemplate question="In what areas do you think the applicant could improve?" :answer="characterInfo.feedback?.improvements" />
          <ReuseQuestionTemplate question="Would you rehire or recommend this person for a similar role? Why or why not?" :answer="characterInfo.feedback?.rehireRecommendation" />
        </div>
      </div>
    </GCollapsibleContent>
  </GCollapsible>
</template>
